import setState from '../utils/storeUtils';
import getFrontConfig from '../../../common/getFrontConfig';
import createPrice from '../../../common/models/priceModel';
import { isObject } from '../../../common/utils/type';

/**
 * @typedef productGiftCardState
 * @type {Object}
 * @property {Boolean} amountError -
 * @property {Object|null} dateToSendRefresh dateToSend keys read from the uncached route
 * @property {Boolean} loading -
 * @property {Object|null} product full product info
 * @property {Object|null} selectedCardAmount -
 * @property {Object|null} selectedCardOption -
 */
const initialState = {
  amountError: false,
  dateToSendRefresh: null,
  loading: false,
  product: null,
  selectedCardAmount: null,
  selectedCardOption: null,
};

export const types = {
  load: 'productGiftCard/load',
  loading: 'productGiftCard/loading',
  refreshDateToSend: 'productGiftCard/refreshDateToSend',
  reset: 'productGiftCard/reset',
  setSelectedCardAmount: 'productGiftCard/setSelectedCardAmount',
  setSelectedCardOption: 'productGiftCard/setSelectedCardOption',
};

export const getters = {
  allowCustomAmount: 'productGiftCard/allowCustomAmount',
  allowScheduledDelivery: 'productGiftCard/allowScheduledDelivery',
  cardOptions: 'productGiftCard/cardOptions',
  form: 'productGiftCard/form',
  predefinedAmounts: 'productGiftCard/predefinedAmounts',
};

export default {
  namespaced: true,
  state: initialState,
  getters: {
    allowCustomAmount: (state) => !!state.product?.allowCustomAmount,
    allowScheduledDelivery: (state) => !!state.product?.allowScheduledDelivery,
    cardOptions: (state) => state.product?.cardsOptions || [],
    /**
     * The form as the page rendered it, with the window laid over it when the
     * uncached route has answered. Composing here keeps the refresh in its own
     * state property instead of writing into the product the page handed over.
     * @param {Object} state Module state
     * @returns {Object} Gift card form
     */
    form: (state) => {
      const form = state.product?.giftCardForm || {};
      if (!state.dateToSendRefresh || !form.dateToSend) return form;
      return {
        ...form,
        dateToSend: { ...form.dateToSend, ...state.dateToSendRefresh },
      };
    },
    predefinedAmounts: (state) => state.product?.predefinedAmounts || [],
  },
  mutations: {
    loading: setState('loading'),
    setProduct: setState('product'),
    setSelectedCardAmount(state, cardAmount) {
      const { minValue, maxValue } =
        state.product?.giftCardForm?.cardAmount || {};
      const currentAmount = isObject(cardAmount)
        ? cardAmount
        : createPrice(cardAmount || 0);
      const isValid =
        !!currentAmount.value &&
        (!minValue || minValue <= currentAmount.value) &&
        (!maxValue || maxValue >= currentAmount.value);

      state.amountError = !isValid;
      state.selectedCardAmount = isValid ? currentAmount : null;
    },
    setSelectedCardOption: setState('selectedCardOption'),
    setDateToSendRefresh: setState('dateToSendRefresh'),
  },
  actions: {
    load({ commit }, product) {
      commit('loading', true);
      commit('setProduct', product);
      const initialCardOption = product?.cardsOptions?.[0];
      if (initialCardOption) commit('setSelectedCardOption', initialCardOption);
      commit('loading', false);
    },
    /**
     * Re-reads the dateToSend field from a route that is not cached. Product-Show
     * is, so the window it renders freezes: past midnight a cached page would
     * hand the shopper a day that is already gone, and the form would accept a
     * date the cart then rejects. Failing leaves the rendered window in place,
     * which is stale at worst and still revalidated on add to cart, and leaves
     * the door open for the next attempt.
     * @param {Object} context Vuex context
     * @returns {Promise} settled once the field has been refreshed or given up on
     */
    refreshDateToSend({ commit, state }) {
      // Asked for when the shopper opens the form, and only the first time: the
      // window does not move while the page is open.
      if (state.dateToSendRefresh) return Promise.resolve();

      const { urls } = getFrontConfig();
      // A refresh that cannot run leaves the rendered window alone rather than
      // breaking the form it is mounted in.
      if (!urls?.giftCardDateToSend || typeof fetch !== 'function') {
        return Promise.resolve();
      }

      return fetch(urls.giftCardDateToSend, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          if (data?.dateToSend) commit('setDateToSendRefresh', data.dateToSend);
        })
        .catch(() => {
          // Keeps the window the page rendered: the cart validates it again
        });
    },
    reset({ commit, state }) {
      const { product } = state;
      commit('setSelectedCardAmount', null);
      const initialCardOption = product?.cardsOptions?.[0];
      if (initialCardOption) commit('setSelectedCardOption', initialCardOption);
    },
  },
};
